import { z } from 'zod';
import { RULES_ENGINE_SUBMISSION_TYPES, RULES_ENGINE_TYPES, RulesEngineType } from './rules-engine.constants';
import { HOLD_TAG_NAME } from './system-tags';

// ---------------------------------------------------------------------------
// Rule structure
//
// A rule is metadata (name/description/enabled) plus a recursive if / else-if / else `Conditional`
// whose branches terminate in an `Outcome`. An outcome is either a list of `Action`s to take, a
// nested `Conditional` (for else-if / deeper branching), or an explicit no-op.
//
// These schemas are the single contract shared by the serializer (rule <-> FHIR Basic), the engine
// evaluator, and the billing app's rule-builder UI. Every rules engine (see RULES_ENGINE_TYPES)
// uses the same rule shape; the `engine` parameter on the CRUD schemas selects which engine's rule
// set an operation targets.
// ---------------------------------------------------------------------------

export const RulesEngineTypeSchema = z.enum(RULES_ENGINE_TYPES);

export const RULE_OPERATORS = [
  'eq',
  'neq',
  'in',
  'notIn',
  'gt',
  'gte',
  'lt',
  'lte',
  'contains',
  'notContains',
  'startsWith',
  'notStartsWith',
  'matches',
  'notMatches',
  'exists',
  'notExists',
] as const;
export type RuleOperator = (typeof RULE_OPERATORS)[number];
export const RuleOperatorSchema = z.enum(RULE_OPERATORS);

// Operator groupings shared by the evaluator and the rule-builder UI.
export const NO_VALUE_OPERATORS: readonly RuleOperator[] = ['exists', 'notExists'];
export const MULTI_VALUE_OPERATORS: readonly RuleOperator[] = ['in', 'notIn'];
// Operators whose value is a fragment of the property (substring/prefix). Value validation only
// requires a non-empty value for these — a partial NPI or ZIP is a legitimate fragment — whereas
// exact-match operators validate the full value against the field's format/options.
export const FRAGMENT_OPERATORS: readonly RuleOperator[] = ['contains', 'notContains', 'startsWith', 'notStartsWith'];
// Operators whose value is a regular expression pattern, not a literal — validation checks that the
// pattern compiles instead of checking it against the field's format/options.
export const REGEX_OPERATORS: readonly RuleOperator[] = ['matches', 'notMatches'];
export const operatorNeedsValue = (op: RuleOperator): boolean => !NO_VALUE_OPERATORS.includes(op);
export const operatorIsMultiValue = (op: RuleOperator): boolean => MULTI_VALUE_OPERATORS.includes(op);
export const operatorTakesFragment = (op: RuleOperator): boolean => FRAGMENT_OPERATORS.includes(op);
export const operatorIsRegex = (op: RuleOperator): boolean => REGEX_OPERATORS.includes(op);

// Operator semantics shared by the rule-builder UI (labels) and the generated documentation
// (labels + descriptions). `dateLabel` overrides the label when the field being compared is a date.
export interface RuleOperatorMetadata {
  label: string;
  dateLabel?: string;
  description: string;
}

export const RULE_OPERATOR_METADATA: Record<RuleOperator, RuleOperatorMetadata> = {
  eq: {
    label: 'equals',
    description:
      'The property exactly equals the value. Number-typed properties compare numerically, so 100 matches 100.00.',
  },
  neq: { label: 'does not equal', description: 'The property does not exactly equal the value.' },
  in: { label: 'is one of', description: 'The property equals one of the listed values.' },
  notIn: { label: 'is not one of', description: 'The property equals none of the listed values.' },
  gt: {
    label: 'is greater than',
    dateLabel: 'is after',
    description: 'The property is greater than the value (numerically for amounts, chronologically for dates).',
  },
  gte: {
    label: 'is at least',
    dateLabel: 'is on or after',
    description: 'The property is greater than or equal to the value.',
  },
  lt: {
    label: 'is less than',
    dateLabel: 'is before',
    description: 'The property is less than the value (numerically for amounts, chronologically for dates).',
  },
  lte: {
    label: 'is at most',
    dateLabel: 'is on or before',
    description: 'The property is less than or equal to the value.',
  },
  contains: {
    label: 'contains',
    description: 'A text property contains the value as a substring; a list property includes the value as an entry.',
  },
  notContains: { label: 'does not contain', description: 'The negation of "contains".' },
  startsWith: {
    label: 'starts with',
    description: 'A text property begins with the value (e.g. member ID starts with XKD).',
  },
  notStartsWith: { label: 'does not start with', description: 'The negation of "starts with".' },
  matches: {
    label: 'matches pattern',
    description:
      'A text property matches the regular expression; a list property matches when any entry does. Standard ' +
      '(unanchored) semantics: the pattern can match anywhere in the value — anchor with ^ and $ to match the ' +
      'whole value, e.g. ^9938[1-7]$ matches exactly CPT codes 99381 through 99387.',
  },
  notMatches: {
    label: 'does not match pattern',
    description:
      'The negation of "matches pattern": a text property does not match the regular expression; a list property ' +
      'matches when no entry does.',
  },
  exists: { label: 'is present', description: 'The property has a (non-empty) value on the claim.' },
  notExists: { label: 'is empty', description: 'The property is missing or empty on the claim.' },
};

export const RULE_LOGIC = ['and', 'or'] as const;
export type RuleLogic = (typeof RULE_LOGIC)[number];

// Discriminator values, named so consumers (schemas, evaluator, UI) don't sprinkle raw strings.
export const RULE_CONDITION_TYPE = { all: 'all', field: 'field', group: 'group' } as const;
export const RULE_ACTION_TYPE = {
  setField: 'setField',
  applyTag: 'applyTag',
  addServiceLine: 'addServiceLine',
  updateServiceLines: 'updateServiceLines',
  removeServiceLines: 'removeServiceLines',
  applyChargeMasterPrices: 'applyChargeMasterPrices',
  noop: 'noop',
} as const;
export const RULE_OUTCOME_TYPE = { actions: 'actions', conditional: 'conditional', noop: 'noop' } as const;

// A condition's comparison value: a scalar (eq/neq/contains), a list (in/notIn), or absent
// (exists/notExists). The evaluator coerces per operator.
const ConditionValueSchema = z.union([z.string(), z.array(z.string())]);
export type RuleConditionValue = z.output<typeof ConditionValueSchema>;

// --- Condition (recursive: a `group` nests conditions) ---
export type RuleCondition =
  | { type: 'all' }
  | { type: 'field'; field: string; operator: RuleOperator; value?: RuleConditionValue }
  | { type: 'group'; logic: RuleLogic; conditions: RuleCondition[] };

export const RuleConditionSchema: z.ZodType<RuleCondition> = z.lazy(() =>
  z.discriminatedUnion('type', [
    z.object({ type: z.literal(RULE_CONDITION_TYPE.all) }),
    z.object({
      type: z.literal(RULE_CONDITION_TYPE.field),
      field: z.string().min(1),
      operator: RuleOperatorSchema,
      value: ConditionValueSchema.optional(),
    }),
    z.object({
      type: z.literal(RULE_CONDITION_TYPE.group),
      logic: z.enum(RULE_LOGIC),
      conditions: z.array(RuleConditionSchema),
    }),
  ])
);

// --- Service line match (the line predicate carried by the service-line actions) ---
//
// Service lines are an array, so the actions that touch them carry their own per-line predicate
// ("update lines where cptCode = X") instead of relying on the rule's condition — a condition can
// detect that a matching line exists, but only the action's match binds *which* lines to change.
// Shaped as a discriminated union so a `group` variant (AND/OR across line properties) can be added
// later without a breaking change.
export const SERVICE_LINE_MATCH_TYPE = { all: 'all', field: 'field' } as const;

export type ServiceLineMatch =
  | { type: 'all' }
  | { type: 'field'; property: string; operator: RuleOperator; value?: RuleConditionValue };

export const ServiceLineMatchSchema: z.ZodType<ServiceLineMatch> = z.discriminatedUnion('type', [
  z.object({ type: z.literal(SERVICE_LINE_MATCH_TYPE.all) }),
  z.object({
    type: z.literal(SERVICE_LINE_MATCH_TYPE.field),
    property: z.string().min(1),
    operator: RuleOperatorSchema,
    value: ConditionValueSchema.optional(),
  }),
]);

// How an updateServiceLines action applies its value: `set` replaces the property (the default);
// `add`/`remove` edit one entry of a list-valued property (modifiers).
export const SERVICE_LINE_SET_OPERATIONS = ['set', 'add', 'remove'] as const;
export type ServiceLineSetOperation = (typeof SERVICE_LINE_SET_OPERATIONS)[number];

// Which claim diagnoses the addServiceLine action points the new line at: the claim's primary
// (first) diagnosis, every diagnosis on the claim, or an explicit set of pointers (diagnosisPointers).
export const DIAGNOSIS_POINTER_MODES = ['primary', 'all', 'specific'] as const;
export type DiagnosisPointerMode = (typeof DIAGNOSIS_POINTER_MODES)[number];

// A serviceDate value can be derived from the claim instead of typed literally. The plain-string form
// (including blank/omitted) is what every rule saved before this option existed already stores, so it
// must keep parsing unchanged; new rules can additionally pick one of these derived sources.
export const DATE_SOURCE_KIND = {
  firstServiceLineDate: 'firstServiceLineDate',
} as const;
export type DateSourceKind = (typeof DATE_SOURCE_KIND)[keyof typeof DATE_SOURCE_KIND];

export const DerivedDateSourceSchema = z.object({ source: z.literal(DATE_SOURCE_KIND.firstServiceLineDate) });
export type DerivedDateSource = z.output<typeof DerivedDateSourceSchema>;

// Likewise, an updateServiceLines placeOfService value can be derived from the claim's service
// facility instead of typed literally — copying whatever CMS place-of-service code is configured on
// the facility (serviceFacility.posCode) rather than a code picked by hand.
export const POS_SOURCE_KIND = {
  facilityPlaceOfService: 'facilityPlaceOfService',
} as const;
export type PosSourceKind = (typeof POS_SOURCE_KIND)[keyof typeof POS_SOURCE_KIND];

export const DerivedPosSourceSchema = z.object({ source: z.literal(POS_SOURCE_KIND.facilityPlaceOfService) });
export type DerivedPosSource = z.output<typeof DerivedPosSourceSchema>;

// A literal ISO date string (default; blank/omitted keeps addServiceLine's legacy implicit
// fallback-to-first-line-date behavior) or a derived source.
export const DateValueSchema = z.union([z.string(), DerivedDateSourceSchema]);
export type DateValue = z.output<typeof DateValueSchema>;

// The updateServiceLines action's set value: a literal string (the default; used as-is for every
// property) or, for the two properties that support it, a derived source — firstServiceLineDate for
// serviceDate, facilityPlaceOfService for placeOfService. Distinct from DateValueSchema (which is
// strictly date-shaped) because this value is shared across every settable service line property, not
// just serviceDate.
export const ServiceLineSetValueSchema = z.union([z.string(), DerivedDateSourceSchema, DerivedPosSourceSchema]);
export type ServiceLineSetValue = z.output<typeof ServiceLineSetValueSchema>;

// The fields of a new service line added by the addServiceLine action. All values are strings (the
// rule value model) and are validated at save time (format) and apply time (claim-dependent checks);
// blank optional fields fall back to the same defaults the claim editor uses — see
// ADD_SERVICE_LINE_FIELDS in the field catalog, which documents each field and drives the UI.
export const AddServiceLineInputSchema = z.object({
  cptCode: z.string().min(1),
  // Comma-separated modifier codes.
  modifiers: z.string().optional(),
  units: z.string().optional(),
  charges: z.string().min(1),
  placeOfService: z.string().optional(),
  serviceDate: DateValueSchema.optional(),
  // Blank/omitted behaves like 'primary' unless diagnosisPointers is set, in which case it behaves
  // like 'specific' — see effectiveDiagnosisMode(), which keeps rules saved before this field
  // existed pointing at the same diagnoses instead of silently switching to 'primary'.
  diagnosisMode: z.enum(DIAGNOSIS_POINTER_MODES).optional(),
  // Comma-separated 1-based pointers into the claim's diagnosis list; only read when diagnosisMode
  // is 'specific'.
  diagnosisPointers: z.string().optional(),
  revenueCode: z.string().optional(),
});
export type AddServiceLineInput = z.output<typeof AddServiceLineInputSchema>;

// The addServiceLine action's diagnosis-selection mode, defaulting blank/omitted input the same way
// the engine and the rule builder both need to: 'specific' when legacy diagnosisPointers is set
// (rules saved before diagnosisMode existed), 'primary' otherwise.
export const effectiveDiagnosisMode = (
  line: Pick<AddServiceLineInput, 'diagnosisMode' | 'diagnosisPointers'>
): DiagnosisPointerMode => line.diagnosisMode ?? (line.diagnosisPointers?.trim() ? 'specific' : 'primary');

// --- Action ---
export type RuleAction =
  | { type: 'setField'; field: string; value: string | null }
  | { type: 'applyTag'; tag: string }
  | { type: 'addServiceLine'; line: AddServiceLineInput }
  | {
      type: 'updateServiceLines';
      match: ServiceLineMatch;
      set: { property: string; value: ServiceLineSetValue; operation?: ServiceLineSetOperation };
    }
  | { type: 'removeServiceLines'; match: ServiceLineMatch }
  // Re-prices the matching lines from the best applicable charge master (resolved by the engine at
  // apply time from the claim's billing type and date of service).
  | { type: 'applyChargeMasterPrices'; match: ServiceLineMatch }
  | { type: 'noop' };

// Tags are free text in the UI, but the engine halts on an exact HOLD_TAG_NAME match — so
// canonicalize case/whitespace variants of the Hold tag here, at the validation boundary.
const tagNameSchema = z
  .string()
  .trim()
  .min(1)
  .transform((tag) => (tag.toLowerCase() === HOLD_TAG_NAME.toLowerCase() ? HOLD_TAG_NAME : tag));

export const RuleActionSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal(RULE_ACTION_TYPE.setField), field: z.string().min(1), value: z.string().nullable() }),
  z.object({ type: z.literal(RULE_ACTION_TYPE.applyTag), tag: tagNameSchema }),
  z.object({ type: z.literal(RULE_ACTION_TYPE.addServiceLine), line: AddServiceLineInputSchema }),
  z.object({
    type: z.literal(RULE_ACTION_TYPE.updateServiceLines),
    match: ServiceLineMatchSchema,
    set: z.object({
      property: z.string().min(1),
      // Empty is meaningful only for list-valued properties ("clear the modifiers"); scalar-property
      // writers reject it at apply time and the engine holds the claim. A derived-source object is
      // only meaningful for the serviceDate property (firstServiceLineDate) and the placeOfService
      // property (facilityPlaceOfService) — save-time validation (field-catalog) rejects a derived
      // source on any other property, since the schema alone can't see the sibling `property` value.
      value: ServiceLineSetValueSchema,
      operation: z.enum(SERVICE_LINE_SET_OPERATIONS).optional(),
    }),
  }),
  z.object({ type: z.literal(RULE_ACTION_TYPE.removeServiceLines), match: ServiceLineMatchSchema }),
  z.object({ type: z.literal(RULE_ACTION_TYPE.applyChargeMasterPrices), match: ServiceLineMatchSchema }),
  z.object({ type: z.literal(RULE_ACTION_TYPE.noop) }),
]);

// --- Outcome / Branch / Conditional (mutually recursive) ---
export type RuleOutcome =
  | { type: 'actions'; actions: RuleAction[] }
  | { type: 'conditional'; conditional: RuleConditional }
  | { type: 'noop' };

export interface RuleBranch {
  condition: RuleCondition;
  outcome: RuleOutcome;
}

export interface RuleConditional {
  branches: RuleBranch[];
  otherwise?: RuleOutcome;
}

export const RuleOutcomeSchema: z.ZodType<RuleOutcome> = z.lazy(() =>
  z.discriminatedUnion('type', [
    z.object({ type: z.literal(RULE_OUTCOME_TYPE.actions), actions: z.array(RuleActionSchema) }),
    z.object({ type: z.literal(RULE_OUTCOME_TYPE.conditional), conditional: RuleConditionalSchema }),
    z.object({ type: z.literal(RULE_OUTCOME_TYPE.noop) }),
  ])
);

export const RuleBranchSchema: z.ZodType<RuleBranch> = z.lazy(() =>
  z.object({
    condition: RuleConditionSchema,
    outcome: RuleOutcomeSchema,
  })
);

export const RuleConditionalSchema: z.ZodType<RuleConditional> = z.lazy(() =>
  z.object({
    branches: z.array(RuleBranchSchema),
    otherwise: RuleOutcomeSchema.optional(),
  })
);

export const BillingRuleSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  description: z.string().default(''),
  enabled: z.boolean().default(true),
  conditional: RuleConditionalSchema,
});
export type BillingRule = z.output<typeof BillingRuleSchema>;

// Save input variant: a new rule arrives without an id — save-billing-rules assigns one server-side,
// so the backend owns rule identifiers.
export const BillingRuleInputSchema = BillingRuleSchema.extend({
  id: z.string().min(1).optional(),
});
export type BillingRuleInput = z.output<typeof BillingRuleInputSchema>;

// --- CRUD API contract (save-billing-rules / get-billing-rules) ---
//
// Every operation names the engine whose rule set it targets.

export const GetBillingRulesInputSchema = z.object({ engine: RulesEngineTypeSchema });
export type GetBillingRulesInput = z.output<typeof GetBillingRulesInputSchema>;

// The whole ordered rule set is saved at once (each engine's rules live in a single FHIR List), so
// create, edit, reorder, and delete are all expressed as "save this full ordered array".
export const SaveBillingRulesInputSchema = z
  .object({
    engine: RulesEngineTypeSchema,
    rules: z.array(BillingRuleInputSchema),
    // Optimistic-locking guard: the List versionId the client last read. When provided, the save is
    // rejected if the rules List changed in the meantime.
    expectedVersionId: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    const seen = new Set<string>();
    data.rules.forEach((rule, index) => {
      if (rule.id == null) return; // new rules get their ids assigned server-side
      if (seen.has(rule.id)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['rules', index, 'id'],
          message: `Duplicate rule id "${rule.id}"`,
        });
      }
      seen.add(rule.id);
    });
  });
export type SaveBillingRulesInput = z.output<typeof SaveBillingRulesInputSchema>;

export interface BillingRulesResponse {
  rules: BillingRule[];
  // List.meta.versionId, echoed so the client can pass it back as expectedVersionId on the next save.
  versionId?: string;
}

export const MAX_RUN_RULES_ENGINE_CLAIMS = 20;

// run-billing-rules-engine: manually kick off the rules engine for one or more existing claims
// (claim detail Submit claim / Prepare for invoice, claims list bulk submit). The backend picks each
// claim's engine from its AR stage and enqueues that engine's Task; a Subscription then runs it
// asynchronously.
export const RunBillingRulesEngineInputSchema = z
  .object({
    claimIds: z.array(z.string().min(1)).min(1).max(MAX_RUN_RULES_ENGINE_CLAIMS),
    skipRules: z.boolean().default(false),
    submissionType: z.enum(RULES_ENGINE_SUBMISSION_TYPES).optional(),
    payerClaimControlNumber: z.string().min(1).optional(),
  })
  .superRefine((data, ctx) => {
    if (!data.skipRules && data.submissionType && data.submissionType !== 'new') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['submissionType'],
        message: `"submissionType" must be "new" when "skipRules" is "false"`,
      });
    }
    if (data.submissionType === 'new' && data.payerClaimControlNumber) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['payerClaimControlNumber'],
        message: `"payerClaimControlNumber" must not be present when "submissionType" is "new"`,
      });
    }
    if ((data.submissionType === 'correction' || data.submissionType === 'void') && !data.payerClaimControlNumber) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['payerClaimControlNumber'],
        message: `"payerClaimControlNumber" is required when "submissionType" is not "new"`,
      });
    }
  });
export type RunBillingRulesEngineInput = z.output<typeof RunBillingRulesEngineInputSchema>;

export interface RunBillingRulesEngineResult {
  claimId: string;
  taskId: string;
  engine: RulesEngineType;
}

export interface RunBillingRulesEngineResponse {
  results: RunBillingRulesEngineResult[];
}
